import React, {Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { NavLink } from 'react-router-dom';
import Validate from 'app/validation/validation';

import './footerNewsletterForm.scss';
//assets
import submit_footer from 'assets/icons/submit_footer.svg';
import cb_empty from 'assets/icons/cb_empty.svg';
import cb_checked from 'assets/icons/cb_checked.svg';
import {getMediaPath, reactParserCommonOptions} from "../../../app/functions";
import AnalyticsManager from "../../../app/analytics";
import ReactHtmlParser from "react-html-parser";
import {useSignUpForNewsletterMutation} from "../../../redux/api/relations.api";
import {
    getUserData,
    setNewsletterStatus,
    setNewsletterSubscriptionResultText,
} from "../../../redux/actions";
import {isMutationFulfilled} from "../../../api/utils";
import {AnalyticsParamValues} from "../../../constants/analytics";

const createInitialForm = (isLoggedIn, userData = null) => ({
    personalId: {
        val: isLoggedIn ? (userData?.user_id || '') : '',
        valid: false,
        rules: isLoggedIn ? [] : ['not_empty', 'id'],
        errMsg: '',
    },
    email: {
        val: isLoggedIn ? (userData?.email || '') : '',
        valid: false,
        rules: ['not_empty', 'email'],
        errMsg: '',
    },
    cellphone: {
        val: isLoggedIn ? (userData?.phone || '') : '',
        valid: false,
        rules: ['cell_optional'],
        errMsg: '',
    },
});

const FooterNewsletterForm = ({
    className = '',
    checkbox_id,
    show_img = false,
    dismissMenu = () => {},
}) => {
    const dispatch = useDispatch();
    const userData = useSelector((store) => store.config.UserData);
    const text = useSelector((store) => store.config.Translations);
    const policy_page_url = useSelector((store) => store.data.policy_page_id);
    const newsletterPopup = useSelector((store) => store.config.newsletterPopup);
    const isSubscribe = useSelector((store) => store.data.Newsletter_subscribe);

    const isLoggedIn = !!userData;
    const hasExistingSubscription = isLoggedIn && !!userData.maStatus;

    const [signUpForNewsletter, newsletterResult] = useSignUpForNewsletterMutation();
    const [showSuccess, setShowSuccess] = useState(false);

    const [firstTry, setFirstTry] = useState(true);
    const [form, setForm] = useState(() => createInitialForm(isLoggedIn, userData));
    const [condition, setCondition] = useState({
        checked: false,
        errMsg: text['newsletter_web_checkbox_error'] || '',
    });

    useEffect(() => {
        setForm(createInitialForm(isLoggedIn, userData));
    }, [userData, isLoggedIn]);

    useEffect(() => {
        if (text['newsletter_web_checkbox_error']) {
            setCondition(prev => ({
                ...prev,
                errMsg: text['newsletter_web_checkbox_error'],
            }));
        }
    }, [text]);

    useEffect(() => {
        if (!isMutationFulfilled(newsletterResult)) return;

        if (isLoggedIn) {
            setShowSuccess(true);
            dispatch(getUserData(false));
        } else {
            dispatch(setNewsletterStatus(newsletterResult.data?.isSuccess));
            dispatch(setNewsletterSubscriptionResultText(newsletterResult.data?.result));
        }

        AnalyticsManager.logSubscribeToNewsletter({
            personalId: form.personalId?.val || '',
            firstName: form.firstName?.val || userData?.first_name || '',
            lastName: form.lastName?.val || userData?.last_name || '',
            cellphone: form.cellphone?.val || '',
            email: form.email?.val || '',
            source: AnalyticsParamValues.NEWS_LETTER_FORM,
            newsLetterResult: newsletterResult.data?.code,
        });
    }, [newsletterResult]);

    const handleSubmit = (e) => {
        AnalyticsManager.logSubscribeToNewsletterEvents();
        e.preventDefault();
        let formValid = true;
        let newForm = {...form};

        for (let field in form) {
            let validate = Validate(form[field].val, form[field].rules);
            let isValid = validate.valid;
            let msg = validate.msg;

            newForm[field].valid = isValid;
            newForm[field].errMsg = msg;

            if (isValid === false) {
                formValid = false;
            }
        }

        setForm(newForm);
        setFirstTry(false);

        if (formValid && condition.checked) {
            let payload = {};
            for (let key in form) {
                payload[key] = form[key].val;
            }
            if (isLoggedIn) {
                payload.firstName = userData.first_name;
                payload.lastName = userData.last_name;
                payload.personalId = userData.user_id;
            }

            signUpForNewsletter(payload);
            AnalyticsManager.logSubscribeToNewsletterSuccess();
        } else {
            AnalyticsManager.logSubscribeToNewsletterFailed();
        }
    };

    const handleInputChange = (e) => {
        const name = e.target.name;
        const val = e.target.value.trim();

        let validate = Validate(val, form[name].rules);
        let isValid = validate.valid;
        let msg = validate.msg;

        setForm(prevForm => ({
            ...prevForm,
            [name]: {...prevForm[name], val, valid: isValid, errMsg: msg},
        }));
    };

    const handleCheckboxOnClick = () => {
        setCondition(prevCondition => ({
            ...prevCondition,
            checked: !prevCondition.checked,
        }));
    };

    const showErrorMsg = !firstTry && (
        !form.personalId?.valid || !form.email?.valid || !form.cellphone?.valid
    );
    const showErrorCB = !firstTry && !condition.checked;

    const renderFields = () => (
        <Fragment>
            {!isLoggedIn && (
                <div className='input-wrapper'>
                    <input className='input-element'
                           type='text'
                           name='personalId'
                           placeholder={text['marketing_personal_id_field']}
                           aria-label={text['marketing_personal_id_field']}
                           value={form.personalId.val}
                           onChange={handleInputChange}/>
                    {showErrorMsg ? <div className='error-msg'>{form.personalId.errMsg}</div> : ''}
                </div>
            )}
            <div className='input-wrapper'>
                <input className='input-element'
                       type='email'
                       name='email'
                       placeholder={text['marketing_email_field']}
                       aria-label={text['marketing_email_field']}
                       value={form.email.val}
                       onChange={handleInputChange}/>
                {showErrorMsg ? <div className='error-msg'>{form.email.errMsg}</div> : ''}
            </div>
            <div className='input-wrapper'>
                <input className='input-element'
                       type='tel'
                       name='cellphone'
                       placeholder={text['marketing_cellphone_field']}
                       aria-label={text['marketing_cellphone_field']}
                       value={form.cellphone.val}
                       onChange={handleInputChange}
                       dir="rtl"/>
                {showErrorMsg ? <div className='error-msg'>{form.cellphone.errMsg}</div> : ''}
            </div>
        </Fragment>
    );

    const checkboxText = isLoggedIn
        ? text['web_newsletter_agreemnet']
        : (newsletterPopup?.formCheckboxes?.[0]?.text || text['web_newsletter_agreemnet']);

    const renderCheckboxAndSubmit = (agreementText) => (
        <div className='form-actions'>
            <div className='cb-wrapper' onClick={handleCheckboxOnClick}>
                <img className='cb-img' src={condition.checked ? getMediaPath(cb_checked) : getMediaPath(cb_empty)} alt='' />
                <input id={checkbox_id}
                       type='checkbox'
                       className='cb'
                       name='conditions'
                       disabled
                       aria-label="מסכים לתנאי השימוש" />
                <label htmlFor={checkbox_id}>
                    <span onClick={e => {
                        if (e) {
                            e.stopPropagation();
                        }
                    }}>{ReactHtmlParser(agreementText, reactParserCommonOptions())}</span>
                    <NavLink onClick={dismissMenu} to={'/' + policy_page_url} className="link">
                        {text.web_to_policy}
                    </NavLink>
                </label>
                {showErrorCB ? <div className='error-msg'>{condition.errMsg}</div> : ''}
            </div>
            <div className='submit-wrapper'>
                <button className='submit-btn'
                        type='submit'
                        name="submit"
                        value=''
                        aria-label="שלח"></button>
                {show_img ? <img className='mail-img' src={getMediaPath(submit_footer)} alt='/'/> :
                    <span className='submit-span'>{text['marketing_footer_submit_button']}</span>}
            </div>
        </div>
    );

    if (isLoggedIn) {
        if (hasExistingSubscription && !showSuccess) {
            return null;
        }

        return (
            <form id="footer-newsletter" className={'news-latter ' + className} onSubmit={handleSubmit}>
                {showSuccess ? (
                    <div className='success'>{text.web_newsletter_success}</div>
                ) : (
                    <Fragment>
                        <span className='label'>{text['marketing_footer_title']}</span>
                        <div className='mail-wrapper'>
                            <div className='form-fields'>
                                {renderFields()}
                            </div>
                            {renderCheckboxAndSubmit(checkboxText)}
                        </div>
                    </Fragment>
                )}
            </form>
        );
    }

    return (
        <form id="footer-newsletter" className={'news-latter ' + className} onSubmit={handleSubmit}>
            {!isSubscribe ? (
                <Fragment>
                    <span className='label'>{text['marketing_footer_title']}</span>
                    <div className='mail-wrapper'>
                        <div className='form-fields'>
                            {renderFields()}
                        </div>
                        {renderCheckboxAndSubmit(checkboxText)}
                    </div>
                </Fragment>
            ) : (
                <div className='success'>{text.web_newsletter_success}</div>
            )}
        </form>
    );
};

export default FooterNewsletterForm;
